import React, { useCallback, useEffect, useMemo, useState } from "react";
import Modal from "react-bootstrap/Modal";
import Table from "react-bootstrap/Table";
import Button from "react-bootstrap/Button";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import { fetchAuditEventsPage, fetchAllAuditEvents } from "../../utils/auditEventsApi";

function formatOccurredAt(iso) {
    if (!iso) return "—";
    try {
        return format(parseISO(iso), "dd/MM/yyyy HH:mm", { locale: es });
    } catch {
        return String(iso);
    }
}

function buildDescription(ev) {
    if (ev.summary && String(ev.summary).trim()) return ev.summary;
    const m = ev.metadata;
    if (!m || typeof m !== "object") return "—";
    const bits = [];
    if (m.delivery_status != null) bits.push(`Envío: ${m.delivery_status}`);
    if (m.template_key) bits.push(String(m.template_key));
    if (m.previous !== undefined && m.new !== undefined) bits.push(`${m.previous} → ${m.new}`);
    if (m.invoice_reference) bits.push(String(m.invoice_reference));
    if (m.file_name) bits.push(String(m.file_name));
    if (m.file_type) bits.push(String(m.file_type));
    if (bits.length) return bits.slice(0, 5).join(" · ");
    try {
        const s = JSON.stringify(m);
        return s.length > 160 ? `${s.slice(0, 157)}…` : s;
    } catch {
        return "—";
    }
}

function stableActionsKey(actionsFilter) {
    if (!Array.isArray(actionsFilter) || actionsFilter.length === 0) return "";
    return [...actionsFilter].sort().join("|");
}

function sanitizeReportFileBase(name) {
    const raw = String(name || "factura").trim() || "factura";
    return raw
        .replace(/[/\\?%*:|"<>[\]]/g, "-")
        .replace(/\s+/g, "_")
        .slice(0, 120);
}

function csvEscapeCell(value) {
    const s = String(value ?? "")
        .replace(/\r\n/g, "\n")
        .replace(/\r/g, "\n");
    if (/[";\n]/.test(s)) {
        return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
}

function approvalsEventsToCsv(events, formatOccurredAtFn, buildDescriptionFn) {
    const sep = ";";
    const header = ["Fecha y Hora", "Usuario", "Evento", "Descripción"];
    const lines = [header.map(csvEscapeCell).join(sep)];
    for (const ev of events) {
        lines.push(
            [
                csvEscapeCell(formatOccurredAtFn(ev.occurred_at)),
                csvEscapeCell(ev.actor_display ?? ""),
                csvEscapeCell(ev.action_label ?? ev.action ?? ""),
                csvEscapeCell(buildDescriptionFn(ev)),
            ].join(sep)
        );
    }
    return `\uFEFF${lines.join("\r\n")}`;
}

/**
 * Modal estilo consulta-oc: tabla sin columna Acciones / sin reenvío de correos.
 */
export default function AuditHistoryModal({
    show,
    onHide,
    mode,
    entityId,
    titleLabel,
    actionsFilter,
}) {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const [hasNext, setHasNext] = useState(false);
    const [exportingReport, setExportingReport] = useState(false);
    const [exportReportError, setExportReportError] = useState("");

    const actionsKey = stableActionsKey(actionsFilter);
    const fetchOptions = useMemo(() => {
        if (!actionsKey) return {};
        return { actions: [...actionsFilter] };
    }, [actionsKey, actionsFilter]);

    const resetAndLoad = useCallback(async () => {
        if (!entityId) return;
        setLoading(true);
        setError(null);
        setPage(1);
        setEvents([]);
        try {
            const { results, next } = await fetchAuditEventsPage(mode, entityId, 1, fetchOptions);
            setEvents(results);
            setHasNext(!!next);
        } catch (e) {
            setError("No se pudo cargar el historial.");
            setEvents([]);
            setHasNext(false);
        } finally {
            setLoading(false);
        }
    }, [mode, entityId, fetchOptions]);

    useEffect(() => {
        if (show && entityId) {
            resetAndLoad();
        }
    }, [show, entityId, resetAndLoad]);

    useEffect(() => {
        if (show) setExportReportError("");
    }, [show]);

    const loadMore = async () => {
        if (!entityId || loadingMore || !hasNext) return;
        const nextPage = page + 1;
        setLoadingMore(true);
        setError(null);
        try {
            const { results, next } = await fetchAuditEventsPage(mode, entityId, nextPage, fetchOptions);
            setEvents((prev) => [...prev, ...results]);
            setPage(nextPage);
            setHasNext(!!next);
        } catch {
            setError("No se pudieron cargar más eventos.");
        } finally {
            setLoadingMore(false);
        }
    };

    const titleRef = titleLabel || (mode === "purchase_order" ? "OC" : "Factura");
    const headline = actionsKey
        ? "Historial de pre-aprobaciones y aprobaciones"
        : "Historial de Eventos";

    const handleDownloadApprovalsReport = async () => {
        if (!entityId || !actionsKey) return;
        setExportReportError("");
        setExportingReport(true);
        try {
            const all = await fetchAllAuditEvents(mode, entityId, fetchOptions);
            const csv = approvalsEventsToCsv(all, formatOccurredAt, buildDescription);
            const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `historial-aprobaciones-${sanitizeReportFileBase(titleRef)}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch {
            setExportReportError("No se pudo generar el reporte.");
        } finally {
            setExportingReport(false);
        }
    };

    return (
        <Modal show={show} onHide={onHide} size="lg" centered scrollable>
            <Modal.Header closeButton style={{ background: "#f9fafb" }}>
                <Modal.Title style={{ fontSize: "1rem", fontWeight: 600 }}>
                    {headline}: <span className="text-primary">{titleRef}</span>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ background: "#fff", padding: "20px" }}>
                {loading && <div className="text-muted small py-3">Cargando…</div>}
                {error && !loading && (
                    <div className="text-danger small py-2">{error}</div>
                )}
                {!loading && !error && events.length === 0 && (
                    <div className="text-muted small py-3">Sin eventos registrados.</div>
                )}
                {!loading && events.length > 0 && (
                    <div className="table-responsive">
                        <Table hover size="sm" className="align-middle mb-0" style={{ fontSize: "13px" }}>
                            <thead style={{ background: "#f8f9fa" }}>
                                <tr>
                                    <th style={{ width: "140px" }}>Fecha y Hora</th>
                                    <th>Usuario</th>
                                    <th>Evento</th>
                                    <th>Descripción</th>
                                </tr>
                            </thead>
                            <tbody>
                                {events.map((ev) => (
                                    <tr key={ev.id}>
                                        <td>{formatOccurredAt(ev.occurred_at)}</td>
                                        <td>{ev.actor_display ?? "—"}</td>
                                        <td>{ev.action_label ?? ev.action ?? "—"}</td>
                                        <td style={{ wordBreak: "break-word" }}>
                                            {buildDescription(ev)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                )}
                {hasNext && (
                    <div className="text-center mt-3">
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            disabled={loadingMore}
                            onClick={loadMore}
                        >
                            {loadingMore ? "Cargando…" : "Cargar más"}
                        </Button>
                    </div>
                )}
                {exportReportError ? (
                    <div className="text-danger small mt-2">{exportReportError}</div>
                ) : null}
            </Modal.Body>
            <Modal.Footer className="d-flex flex-wrap gap-2 justify-content-end align-items-center">
                {actionsKey ? (
                    <Button
                        variant="outline-primary"
                        size="sm"
                        disabled={exportingReport || loading || !entityId}
                        onClick={handleDownloadApprovalsReport}
                    >
                        {exportingReport ? "Exportando…" : "Descargar reporte"}
                    </Button>
                ) : null}
                <Button variant="secondary" onClick={onHide}>
                    Cerrar
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
